import { useEffect, useState } from "react";
import logo from "../assets/logo-dark.svg";
import api from "../lib/api.js";
import { useAuth } from "../lib/auth.jsx";
import { Field } from "../components/ui.jsx";
import { IconBolt, IconCheck, IconWarning } from "../components/icons.jsx";

// Mirrors the server's lockout policy in internal/operators/service.go.
const MAX_ATTEMPTS = 5;
const WARN_AFTER = 3;
const CODE_LENGTH = 6;

export default function Login() {
  const { signIn } = useAuth();
  const [staging, setStaging] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api.authConfig().then((config) => { if (!cancelled) setStaging(config.staging_login); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // pending holds the challenge returned once the password is accepted. Its
  // presence is what moves the form to the code step; there is no separate
  // step counter to fall out of sync with it.
  const [pending, setPending] = useState(null);
  const [code, setCode] = useState("");
  // mode is "signin" or "recover". Both steps share the code field, and
  // which one is running decides what the code is spent on. The server
  // records the same distinction against the code itself, so a mismatch here
  // is refused rather than silently doing the wrong thing.
  const [mode, setMode] = useState("signin");
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [done, setDone] = useState(null);
  // Failures are counted per address, because typing the wrong address three
  // times does not bring any one account closer to being locked.
  const [failures, setFailures] = useState({ account: "", count: 0 });

  async function submitPassword(event) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    const account = email.trim().toLowerCase();
    try {
      const result = await api.beginLogin(account, password);
      setPending(result);
      setPassword("");
      setFailures({ account: "", count: 0 });
    } catch (err) {
      // The server deliberately returns the same message for an unknown
      // account and a wrong password, and the console must not undo that by
      // guessing which one it was.
      setError(err.message);
      setFailures((prev) => ({
        account,
        count: prev.account === account ? prev.count + 1 : 1,
      }));
    } finally {
      setBusy(false);
    }
  }

  async function submitForgot(event) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await api.forgotPassword(email.trim().toLowerCase());
      setPending(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(event) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(pending.challenge_id, code.trim());
    } catch (err) {
      setError(err.message);
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  async function submitReset(event) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.resetPasswordWithCode(pending.challenge_id, code.trim(), newPassword);
      setDone("Your password has been changed. Sign in with the new one.");
      setPending(null);
      setMode("signin");
      setCode("");
      setNewPassword("");
      setRepeatPassword("");
    } catch (err) {
      setError(err.message);
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  function startOver() {
    setPending(null);
    setCode("");
    setNewPassword("");
    setRepeatPassword("");
    setError(null);
  }

  function beginRecovery() {
    setMode("recover");
    setPending(null);
    setPassword("");
    setError(null);
    setDone(null);
  }

  function backToSignIn() {
    setMode("signin");
    setPending(null);
    setCode("");
    setError(null);
  }

  const resetValid =
    code.length === CODE_LENGTH &&
    newPassword.length >= 12 &&
    newPassword === repeatPassword;

  // Nobody needs to be told about the lockout while they are signing in
  // normally, and saying so up front only tells an attacker how many guesses
  // they get. The warning appears once someone is close enough for it to be
  // useful, and only while it is still avoidable.
  const attemptsLeft = MAX_ATTEMPTS - failures.count;
  const warnAboutLockout =
    failures.account === email.trim().toLowerCase() &&
    failures.count >= WARN_AFTER &&
    attemptsLeft > 0;

  return (
    <div className="login-screen">
      <aside className="login-aside">
        <svg className="login-mesh" viewBox="0 0 400 600" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="meshLine" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#6366F1" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#16BFD8" stopOpacity="0.35" />
            </linearGradient>
            <radialGradient id="nodeGlow">
              <stop offset="0%" stopColor="#16BFD8" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#16BFD8" stopOpacity="0" />
            </radialGradient>
          </defs>
          <g stroke="url(#meshLine)" strokeWidth="0.7" fill="none">
            <path d="M0 120 Q100 80 200 120 T400 120" />
            <path d="M0 240 Q100 200 200 240 T400 240" />
            <path d="M0 360 Q100 320 200 360 T400 360" />
            <path d="M0 480 Q100 440 200 480 T400 480" />
            <path d="M60 60 L120 180 L60 300 L120 420 L60 540" />
            <path d="M180 60 L240 180 L180 300 L240 420 L180 540" />
            <path d="M300 60 L360 180 L300 300 L360 420 L300 540" />
          </g>
          {[
            [60, 120],
            [200, 240],
            [340, 180],
            [120, 420],
            [300, 480],
          ].map(([cx, cy]) => (
            <g key={`${cx}-${cy}`}>
              <circle cx={cx} cy={cy} r="14" fill="url(#nodeGlow)" />
              <circle cx={cx} cy={cy} r="3" fill="#22D3EE" />
            </g>
          ))}
        </svg>

        <div className="login-brand">
          <img src={logo} alt="MeshNova" />
        </div>

        <div>
          <h2>
            Head-end console for
            <br />
            mesh-grid operations
          </h2>
          <p style={{ marginBottom: 26 }}>
            Register hardware, control devices in the field, issue prepaid tokens and
            follow consumption across every tenant on the platform.
          </p>

          <div className="login-features">
            <div className="login-feature">
              <IconCheck />
              <span>Branch, Branch+, NodeFlex, NodeNet, PowerShare and NovaLink in one registry</span>
            </div>
            <div className="login-feature">
              <IconBolt />
              <span>Direct device commands with full acknowledgement tracking</span>
            </div>
            <div className="login-feature">
              <IconWarning />
              <span>Offline token vending when a device has no radio link</span>
            </div>
          </div>
        </div>

        <p style={{ fontSize: 12, opacity: 0.55 }}>
          © {new Date().getFullYear()} MeshNova Grid Solutions
        </p>
      </aside>

      <div className="login-panel">
        {pending && mode === "recover" ? (
          <form className="login-form" onSubmit={submitReset}>
            <h1>Set a new password</h1>
            <p>
              If <strong>{pending.sent_to}</strong> is a MeshNova account, a{" "}
              {CODE_LENGTH}-digit code is on its way. Enter it with the password you want.
            </p>

            {error && (
              <div className="callout callout-danger" role="alert">
                <IconWarning />
                <div>{error}</div>
              </div>
            )}

            <Field label="Recovery code" required>
              <input
                className="input code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={CODE_LENGTH}
                autoFocus
                required
              />
            </Field>

            <Field
              label="New password"
              required
              error={
                newPassword.length > 0 && newPassword.length < 12
                  ? "At least twelve characters."
                  : null
              }
            >
              <input
                className="input"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </Field>

            <Field
              label="Repeat new password"
              required
              error={
                repeatPassword.length > 0 && repeatPassword !== newPassword
                  ? "The two entries do not match."
                  : null
              }
            >
              <input
                className="input"
                type="password"
                value={repeatPassword}
                onChange={(e) => setRepeatPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </Field>

            <button className="btn btn-primary btn-block" disabled={busy || !resetValid}>
              {busy ? <span className="spinner" /> : null}
              {busy ? "Setting your password" : "Set password"}
            </button>

            <button
              type="button"
              className="btn btn-ghost btn-block"
              style={{ marginTop: 8 }}
              onClick={backToSignIn}
            >
              Back to sign in
            </button>

            <p className="hint" style={{ marginTop: 18, textAlign: "center" }}>
              Every session on this account will be signed out.
            </p>
          </form>
        ) : mode === "recover" ? (
          <form className="login-form" onSubmit={submitForgot}>
            <h1>Forgotten password</h1>
            <p>
              Tell us the address on your account and we will email you a code to set a new
              password.
            </p>

            {error && (
              <div className="callout callout-danger" role="alert">
                <IconWarning />
                <div>{error}</div>
              </div>
            )}

            <Field label="Email address" required>
              <input
                className="input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
            </Field>

            <button className="btn btn-primary btn-block" disabled={busy || !email}>
              {busy ? <span className="spinner" /> : null}
              {busy ? "Sending your code" : "Send recovery code"}
            </button>

            <button
              type="button"
              className="btn btn-ghost btn-block"
              style={{ marginTop: 8 }}
              onClick={backToSignIn}
            >
              Back to sign in
            </button>

            <p className="hint" style={{ marginTop: 18, textAlign: "center" }}>
              We answer the same way whether or not the address has an account, so this
              page cannot be used to find out who works here.
            </p>
          </form>
        ) : pending ? (
          <form className="login-form" onSubmit={submitCode}>
            <h1>{pending.test_code ? "Your staging sign-in code" : "Check your email"}</h1>
            <p>
              {pending.test_code ? <>Use <strong>{pending.test_code}</strong> to finish signing in to staging. No email access is needed.</> : <>We sent a {CODE_LENGTH}-digit code to <strong>{pending.sent_to}</strong>. It can be used once and expires shortly.</>}
            </p>

            {error && (
              <div className="callout callout-danger" role="alert">
                <IconWarning />
                <div>{error}</div>
              </div>
            )}

            <Field label="Sign-in code" required>
              <input
                className="input code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern={`\\d{${CODE_LENGTH}}`}
                maxLength={CODE_LENGTH}
                autoFocus
                required
              />
            </Field>

            <button
              className="btn btn-primary btn-block"
              disabled={busy || code.length !== CODE_LENGTH}
            >
              {busy ? <span className="spinner" /> : null}
              {busy ? "Checking" : "Sign in"}
            </button>

            <button
              type="button"
              className="btn btn-ghost btn-block"
              style={{ marginTop: 8 }}
              onClick={startOver}
            >
              Use a different account
            </button>

            <p className="hint" style={{ marginTop: 18, textAlign: "center" }}>
              {pending.test_code ? "This shared code works only in the staging environment. Your account password is still required." : "Did not ask for this code? Change your password and contact your administrator."}
            </p>
          </form>
        ) : (
          <form className="login-form" onSubmit={submitPassword}>
            <h1>Sign in</h1>
            <p>Use the operator account issued to you by a system administrator.</p>
            {staging && <div className="callout callout-warning" role="status"><IconWarning /><div>Staging workspace. A shared verification code appears after your password is accepted.</div></div>}

            {done && (
              <div className="callout callout-success" role="status">
                <IconCheck />
                <div>{done}</div>
              </div>
            )}

            {error && (
              <div className="callout callout-danger" role="alert">
                <IconWarning />
                <div>{error}</div>
              </div>
            )}

            {warnAboutLockout && (
              <div className="callout callout-warning" role="status">
                <IconWarning />
                <div>
                  {attemptsLeft === 1
                    ? "One more failed attempt will lock this account for fifteen minutes."
                    : `${attemptsLeft} more failed attempts will lock this account for fifteen minutes.`}
                </div>
              </div>
            )}

            <Field label="Email address" required>
              <input
                className="input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
            </Field>

            <Field label="Password" required>
              <input
                className="input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </Field>

            <button className="btn btn-primary btn-block" disabled={busy || !email || !password}>
              {busy ? <span className="spinner" /> : null}
              {busy ? "Checking your account" : "Continue"}
            </button>

            <button
              type="button"
              className="btn btn-ghost btn-block"
              style={{ marginTop: 8 }}
              onClick={beginRecovery}
            >
              Forgotten your password?
            </button>

            <p className="hint" style={{ marginTop: 18, textAlign: "center" }}>
              {staging ? "Use your assigned test account. No email access is needed to sign in." : <>We will email you a {CODE_LENGTH}-digit code to finish signing in.</>}
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
